<template>
  <component
    :is="tag"
    ref="rootEl"
    :class="['scroll-reveal', { 'scroll-reveal--hidden': isHidden, 'scroll-reveal--visible': !isHidden }]"
    :style="revealStyle"
  >
    <!-- Default slot is always rendered, so content is present and visible even
         if JavaScript or the Intersection Observer never run (Requirement 8.7). -->
    <slot />
  </component>
</template>

<script setup>
// ScrollReveal — a subtle scroll-reveal wrapper for the redesigned marketing
// site. It uses the Intersection Observer API to fade/translate its content
// into view as it enters the viewport.
//
// Key guarantees (Requirements 8.7, 9.7):
//   - The default slot renders immediately and is never hidden from no-JS or
//     observer-less environments — the pre-reveal hidden state is only applied
//     after mount, once we know JS is running and motion is permitted.
//   - When the user requests reduced motion, no fade/translate is applied:
//     content is shown immediately with no transition.

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useReducedMotion } from '@/composables/useReducedMotion'

const props = defineProps({
  // Element/component to render as the wrapper.
  tag: {
    type: String,
    default: 'div'
  },
  // Fraction of the element that must be visible before it reveals.
  threshold: {
    type: Number,
    default: 0.15
  },
  // Margin around the root used to grow/shrink the intersection area.
  rootMargin: {
    type: String,
    default: '0px 0px -10% 0px'
  },
  // Reveal only once (default) or re-hide when scrolled back out of view.
  once: {
    type: Boolean,
    default: true
  },
  // Transition duration in milliseconds.
  duration: {
    type: Number,
    default: 600
  },
  // Vertical translate distance (px) applied before reveal.
  distance: {
    type: Number,
    default: 24
  },
  // Optional reveal delay in milliseconds.
  delay: {
    type: Number,
    default: 0
  }
})

const { prefersReducedMotion } = useReducedMotion()

const rootEl = ref(null)
// Tracks whether the content has been revealed. Starts revealed so that, until
// the observer is wired up after mount, content is fully visible.
const isRevealed = ref(true)
// Whether we are actively running the reveal animation for this instance.
const isAnimating = ref(false)

let observer = null

// Motion runs only when the environment supports it and the user has not
// requested reduced motion.
const canAnimate = computed(() =>
  !prefersReducedMotion.value &&
  typeof window !== 'undefined' &&
  typeof window.IntersectionObserver === 'function'
)

// The element is hidden only while we are animating and it has not yet revealed.
const isHidden = computed(() => isAnimating.value && !isRevealed.value)

const revealStyle = computed(() => {
  if (!isAnimating.value) {
    return {}
  }
  return {
    '--scroll-reveal-distance': `${props.distance}px`,
    transition: `opacity ${props.duration}ms ease-out ${props.delay}ms, transform ${props.duration}ms ease-out ${props.delay}ms`,
    willChange: 'opacity, transform'
  }
})

function reveal () {
  isRevealed.value = true
}

function hide () {
  isRevealed.value = false
}

onMounted(() => {
  if (!canAnimate.value) {
    // Reduced motion or no Intersection Observer: leave content visible with no
    // animation. Nothing further to wire up.
    return
  }

  // We can animate: switch into the pre-reveal hidden state before observing.
  isAnimating.value = true
  isRevealed.value = false

  observer = new window.IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          reveal()
          if (props.once && observer) {
            observer.disconnect()
            observer = null
          }
        } else if (!props.once) {
          hide()
        }
      })
    },
    {
      threshold: props.threshold,
      rootMargin: props.rootMargin
    }
  )

  if (rootEl.value) {
    observer.observe(rootEl.value)
  } else {
    // Defensive: if the ref is somehow unavailable, reveal so content is shown.
    reveal()
  }
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
})
</script>

<style scoped>
.scroll-reveal--hidden {
  opacity: 0;
  transform: translateY(var(--scroll-reveal-distance, 24px));
}

.scroll-reveal--visible {
  opacity: 1;
  transform: translateY(0);
}
</style>
